import type { ColDef, ColGroupDef } from 'ag-grid-community';

import { isGroupDef } from './fromAgColDefs';

// Where the user (or an owner, through applyColumnState) has moved and hidden
// columns, over what the column definitions say
export type ColumnLayout = {
  // Column ids in the order to show them. Columns not listed follow, in the
  // order the definitions give them
  order?: string[];
  // Shown (false) or hidden (true), in place of the definition's `hide`
  hidden: { [colId: string]: boolean };
};

export const EMPTY_COLUMN_LAYOUT: ColumnLayout = { hidden: {} };

const idOf = (def: ColDef) => def.colId ?? def.field;

const orderDefs = ({
  defs,
  order,
}: {
  defs: (ColDef | ColGroupDef)[];
  order: string[];
}) => {
  // A group sits where its first listed column does, so its columns stay
  // together
  const positionOf = (def: ColDef | ColGroupDef): number => {
    if (isGroupDef(def)) {
      return Math.min(order.length, ...def.children.map(positionOf));
    }
    const id = idOf(def);
    const position = id ? order.indexOf(id) : -1;
    return position === -1 ? order.length : position;
  };
  // Stable: unlisted columns keep their order, after the listed ones
  return defs
    .map((def, index) => ({ def, index }))
    .sort((a, b) => positionOf(a.def) - positionOf(b.def) || a.index - b.index)
    .map(({ def }) => def);
};

// The column definitions with the layout applied: listed columns in the
// layout's order (within each group, and each group where its first column
// is), and `hide` from the layout where it says. Pinned columns still come
// first or last (fromAgColDefs puts them there), so a move never crosses a
// pin.
const applyColumnLayout = ({
  columnDefs,
  layout,
}: {
  columnDefs: (ColDef | ColGroupDef)[];
  layout: ColumnLayout;
}): (ColDef | ColGroupDef)[] => {
  const { order, hidden } = layout;
  if (!order && Object.keys(hidden).length === 0) {
    return columnDefs;
  }
  const apply = (defs: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] => {
    const applied = defs.map((def): ColDef | ColGroupDef => {
      if (isGroupDef(def)) {
        return { ...def, children: apply(def.children) };
      }
      const id = idOf(def);
      return id !== undefined && hidden[id] !== undefined
        ? { ...def, hide: hidden[id] }
        : def;
    });
    return order ? orderDefs({ defs: applied, order }) : applied;
  };
  return apply(columnDefs);
};

export default applyColumnLayout;
